import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, KeyRound, Plus, Puzzle, RotateCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import useIntegrationKeys from '../hooks/useIntegrationKeys';
import { CreatedIntegrationKey, integrationService } from '../services/integrationService';
import useToastStore from '../store/useToastStore';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  danger?: boolean;
  busy?: boolean;
}

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, danger, busy }: ConfirmationModalProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <p className="mb-6 text-gray-600">{message}</p>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto disabled:opacity-50">
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 text-white rounded-lg w-full sm:w-auto disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {busy ? 'Working…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// One-time reveal of a freshly created/rotated key. Once this modal is closed the
// raw secret is gone for good — only its last 4 chars remain visible in the list.
const RevealKeyModal = ({ created, onClose }: { created: CreatedIntegrationKey | null; onClose: () => void }) => {
  const [copied, setCopied] = useState(false);
  const toast = useToastStore();
  if (!created) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.showToast('Could not copy to clipboard', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <KeyRound className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold">Your new API key</h2>
        </div>

        <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Copy this key now and store it in the IMS. For security it is shown only once — you won't be able to see it again.
          </p>
        </div>

        <div className="flex items-stretch gap-2 mb-6">
          <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 break-all font-mono">
            {created.key}
          </code>
          <button
            onClick={copy}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1.5 text-sm font-medium flex-shrink-0"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium">
            I've saved it
          </button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

const IntegrationsPage = () => {
  const { data: keys, isLoading, refetch } = useIntegrationKeys();
  const queryClient = useQueryClient();
  const toast = useToastStore();

  const [showGenerate, setShowGenerate] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedIntegrationKey | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [rotateId, setRotateId] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integration-keys'] });

  const handleGenerate = async () => {
    try {
      setBusy(true);
      const result = await integrationService.createKey(label.trim() || undefined);
      setShowGenerate(false);
      setLabel('');
      setCreated(result);
      invalidate();
      toast.showToast('API key created', 'success');
    } catch (error) {
      toast.showToast(error instanceof Error ? error.message : 'Failed to create key', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!rotateId) return;
    try {
      setBusy(true);
      const result = await integrationService.rotateKey(rotateId);
      setRotateId(null);
      setCreated(result);
      invalidate();
      toast.showToast('API key rotated', 'success');
    } catch (error) {
      toast.showToast(error instanceof Error ? error.message : 'Failed to rotate key', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    try {
      setBusy(true);
      await integrationService.revokeKey(revokeId);
      setRevokeId(null);
      invalidate();
      toast.showToast('API key revoked', 'success');
    } catch (error) {
      toast.showToast(error instanceof Error ? error.message : 'Failed to revoke key', 'error');
    } finally {
      setBusy(false);
    }
  };

  const activeKeys = (keys ?? []).filter((k) => k.status === 'active');
  const revokedKeys = (keys ?? []).filter((k) => k.status === 'revoked');

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Intro */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex-shrink-0 flex items-center justify-center">
                <Puzzle className="h-5 w-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Inventory (IMS) Integration</h3>
            </div>
            <p className="text-sm text-gray-600">
              Connect an external inventory management system to sync stock and orders with this store.
              Generate an API key below and paste it into the IMS to authorize the connection.
            </p>
          </div>
        </div>

        {/* API Keys */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                  <KeyRound className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">API Keys</h3>
              </div>
              <button
                onClick={() => setShowGenerate(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Generate key
              </button>
            </div>

            {isLoading ? (
              <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
            ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
              <div className="text-center py-8">
                <KeyRound className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No API keys yet. Generate one to connect your IMS.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {[...activeKeys, ...revokedKeys].map((k) => {
                  const revoked = k.status === 'revoked';
                  return (
                    <div key={k._id} className="py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 truncate">{k.label || 'InventoryOS key'}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${revoked ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}
                          >
                            {revoked ? 'Revoked' : 'Active'}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                          <span className="font-mono">m24_…{k.last4}</span>
                          <span>Created {formatDate(k.createdAt)}</span>
                          <span>Last used {formatDate(k.lastUsedAt)}</span>
                        </div>
                      </div>
                      {!revoked && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setRotateId(k._id)}
                            title="Rotate key"
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <RotateCw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setRevokeId(k._id)}
                            title="Revoke key"
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generate modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Generate API key</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. InventoryOS"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 mb-6"
            />
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                onClick={() => { setShowGenerate(false); setLabel(''); }}
                disabled={busy}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto disabled:opacity-50"
              >
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reveal-once modal */}
      <RevealKeyModal created={created} onClose={() => { setCreated(null); refetch(); }} />

      {/* Rotate confirm */}
      <ConfirmationModal
        isOpen={!!rotateId}
        onClose={() => setRotateId(null)}
        onConfirm={handleRotate}
        title="Rotate API key?"
        message="This generates a new key and immediately revokes the current one. Update the IMS with the new key or the connection will stop working."
        confirmText="Rotate"
        cancelText="Cancel"
        busy={busy}
      />

      {/* Revoke confirm */}
      <ConfirmationModal
        isOpen={!!revokeId}
        onClose={() => setRevokeId(null)}
        onConfirm={handleRevoke}
        title="Revoke API key?"
        message="Any IMS using this key will immediately lose access. This cannot be undone."
        confirmText="Revoke"
        cancelText="Cancel"
        danger
        busy={busy}
      />
    </div>
  );
};

export default IntegrationsPage;
